import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Brain, Pencil, Trash2, Check, X, Plus, Loader2 } from "lucide-react";
import api from "../lib/api";

const CATS = [
  { k: "people", l: "People", c: "text-sky-300 border-sky-400/40 bg-sky-400/10" },
  { k: "goals", l: "Goals", c: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  { k: "fears", l: "Fears", c: "text-rose-300 border-rose-400/40 bg-rose-400/10" },
  { k: "patterns", l: "Patterns", c: "text-violet-300 border-violet-400/40 bg-violet-400/10" },
  { k: "preferences", l: "Preferences", c: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  { k: "other", l: "Other", c: "text-muted-foreground border-border" },
];
const KEYS = CATS.map((c) => c.k);
const mapKey = (c) => (KEYS.includes(c) ? c : "other");
const catInfo = (c) => CATS.find((x) => x.k === mapKey(c));

function CatSelect({ value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="text-xs rounded-md border border-border bg-transparent px-2 py-1.5 outline-none focus:border-primary">
      {CATS.map((c) => <option key={c.k} value={c.k} className="bg-background">{c.l}</option>)}
    </select>
  );
}

export default function MemoryManager({ profileId, reloadSignal }) {
  const [memories, setMemories] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [draftCat, setDraftCat] = useState("other");
  const [adding, setAdding] = useState("");
  const [addCat, setAddCat] = useState("other");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!profileId) return;
    try {
      const { data } = await api.get(`/profiles/${profileId}/memories`);
      setMemories(data);
    } catch { setMemories([]); }
  }, [profileId]);

  useEffect(() => { load(); }, [load, reloadSignal]);

  const startEdit = (m) => { setEditingId(m.id); setDraft(m.content); setDraftCat(mapKey(m.category)); };
  const cancelEdit = () => { setEditingId(null); setDraft(""); };

  const saveEdit = async (id) => {
    const content = draft.trim();
    if (!content) return;
    setBusy(true);
    try {
      await api.patch(`/profiles/${profileId}/memories/${id}`, { content, category: draftCat });
      setMemories((ms) => ms.map((m) => (m.id === id ? { ...m, content, category: draftCat } : m)));
      cancelEdit();
      toast.success("Memory updated");
    } catch { toast.error("Could not update"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/profiles/${profileId}/memories/${id}`);
      setMemories((ms) => ms.filter((m) => m.id !== id));
      toast.success("Memory forgotten");
    } catch { toast.error("Could not delete"); } finally { setBusy(false); }
  };

  const add = async () => {
    const content = adding.trim();
    if (!content) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/profiles/${profileId}/memories`, { content, category: addCat });
      setMemories((ms) => [data, ...(ms || [])]);
      setAdding("");
      toast.success("Your companion will remember this");
    } catch { toast.error("Could not add"); } finally { setBusy(false); }
  };

  const counts = (memories || []).reduce((a, m) => { const k = mapKey(m.category); a[k] = (a[k] || 0) + 1; return a; }, {});
  const shown = (memories || []).filter((m) => filter === "all" || mapKey(m.category) === filter);

  return (
    <div className="panel p-5" data-testid="memory-manager">
      <p className="overline mb-1 flex items-center gap-2"><Brain size={14} /> What I remember</p>
      <p className="text-[11px] text-muted-foreground mb-3">Tag, correct, add, or forget anything your companion holds about you.</p>

      {/* add new */}
      <div className="space-y-1.5 mb-3">
        <div className="flex gap-1.5">
          <input data-testid="memory-add-input" value={adding} onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Tell it something to remember…"
            className="flex-1 px-3 py-2 rounded-lg border border-border focus:border-primary outline-none text-sm bg-transparent" />
          <button data-testid="memory-add-btn" onClick={add} disabled={busy || !adding.trim()}
            className="px-2.5 rounded-lg border border-border hover:border-primary/50 text-primary disabled:opacity-40 transition-colors">
            <Plus size={16} />
          </button>
        </div>
        <CatSelect value={addCat} onChange={setAddCat} testid="memory-add-category" />
      </div>

      {/* filter chips */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="memory-filters">
        <button data-testid="memory-filter-all" onClick={() => setFilter("all")}
          className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${filter === "all" ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
          All {memories ? `· ${memories.length}` : ""}
        </button>
        {CATS.map((c) => (
          <button key={c.k} data-testid={`memory-filter-${c.k}`} onClick={() => setFilter(c.k)}
            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${filter === c.k ? c.c : "border-border text-muted-foreground hover:text-foreground"}`}>
            {c.l}{counts[c.k] ? ` · ${counts[c.k]}` : ""}
          </button>
        ))}
      </div>

      {memories === null ? (
        <Loader2 size={16} className="animate-spin text-primary" />
      ) : memories.length === 0 ? (
        <p className="text-xs text-muted-foreground">No memories yet — complete a companion session and insights will appear here.</p>
      ) : shown.length === 0 ? (
        <p className="text-xs text-muted-foreground">No memories tagged "{catInfo(filter)?.l || filter}" yet.</p>
      ) : (
        <div className="space-y-2 max-h-[22rem] overflow-auto">
          {shown.map((m) => {
            const info = catInfo(m.category);
            return (
              <div key={m.id} data-testid={`memory-item-${m.id}`}
                className="group rounded-lg border border-border p-2.5 text-sm">
                {editingId === m.id ? (
                  <div className="space-y-2">
                    <textarea data-testid={`memory-edit-input-${m.id}`} value={draft} onChange={(e) => setDraft(e.target.value)}
                      rows={3} className="w-full px-2 py-1.5 rounded-md border border-primary/50 bg-transparent outline-none text-sm resize-none" />
                    <div className="flex items-center justify-between gap-1.5">
                      <CatSelect value={draftCat} onChange={setDraftCat} testid={`memory-edit-category-${m.id}`} />
                      <div className="flex gap-1.5">
                        <button data-testid={`memory-save-${m.id}`} onClick={() => saveEdit(m.id)} disabled={busy}
                          className="p-1.5 rounded-md bg-primary/15 text-primary hover:bg-primary/25"><Check size={14} /></button>
                        <button data-testid={`memory-cancel-${m.id}`} onClick={cancelEdit}
                          className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground"><X size={14} /></button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-foreground/90 leading-snug flex-1">{m.content}</p>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button data-testid={`memory-edit-${m.id}`} onClick={() => startEdit(m)}
                          className="p-1 rounded text-muted-foreground hover:text-primary"><Pencil size={13} /></button>
                        <button data-testid={`memory-delete-${m.id}`} onClick={() => remove(m.id)} disabled={busy}
                          className="p-1 rounded text-muted-foreground hover:text-accent"><Trash2 size={13} /></button>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span data-testid={`memory-cat-${m.id}`} className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${info.c}`}>{info.l}</span>
                      {m.source === "user" && <span className="text-[9px] font-mono uppercase tracking-widest text-accent/70">added by you</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
